import {useState, useEffect} from 'react';
import {isDefined} from './../../types/type-guards';

export type UseArrayTogglerType<T extends unknown> = [
  (data: T) => boolean,
  (data: T) => void,
  () => void,
];

export enum ArrayTogglerBehaviorTypes {
  singleSelect = 'singleSelect',
  multiSelect = 'multiSelect',
  radio = 'radio',
}

/**
 * useArrayToggler can be used for the following use cases -
 * - checkBoxList: list of check box items, single selection or multi selection
 * - tabs: tabs behavior of toggling between tab from list of items
 * @param dataList dataList on which selection behavior is to be attached
 * @param lookup function which allows to lookup key of each item in dataList, e.g. - (item) => item or (item) => item.key
 * @param arrayTogglerBehaviorType selection behavior for the given list like singleSelect | multiSelect | radio
 * to pass value, must import enum from this module - import {ArrayToggleBehaviorTypes}
 * @param callback callback function to be called on interaction with dataList item, with current state of dataList
 * @param defaultStateOfDataList defaultState of dataList, if there are any pre-selections to be displayed
 * @return isChecked - method to check if item is active in the list
 * @return onClickHandler - method to check if item is active in the list
 * @example
 * const [isChecked, onClickHandler, resetHandler] = useArrayToggler(
    ['apple', 'mango', 'orange'], // dataList
    (item) => item, // lookup function
    ArrayTogglerBehaviorTypes.multiSelect, // Array toggle behavior type, import this enum to pass value
    (['selected item(s) list']) => dispatchSomeAction(),
    ['orange (to be pre-selected)'], // defaultStateOfDataList
  );
  data.map((item, index) => (
            <div key={name + String(index)}>
              <div onClick={() => onClickHandler(item)}>
                {(isChecked(item) && <CheckBoxCheckedIcon />) || <CheckBoxUncheckedIcon />}
                {item.value}
              </div>
            </div>
          ))}
 */
const useArrayToggler = <T extends unknown, K extends keyof T>(
  dataList: T[],
  lookup: (item: T) => T[K] | T,
  arrayTogglerBehaviorType: keyof typeof ArrayTogglerBehaviorTypes,
  callback: (dataListTracker: T[], itemUnderAction: T, itemSelected: boolean) => void,
  defaultStateOfDataList?: T[],
): UseArrayTogglerType<T> => {
  const [dataListTracker, setDataListTracker] = useState<T[]>(
    (isDefined(defaultStateOfDataList) && defaultStateOfDataList) || [],
  );

  useEffect(() => {
    setDataListTracker(defaultStateOfDataList || []);
  }, [defaultStateOfDataList]);

  const onClickHandler = (dataListItem: T) => {
    const isRadio = ArrayTogglerBehaviorTypes.radio === arrayTogglerBehaviorType;
    const isMultiSelect = ArrayTogglerBehaviorTypes.multiSelect === arrayTogglerBehaviorType;

    let nextStateOfDataListTracker: T[] = [...dataListTracker];

    const isPresentInDataListTracker = dataListTracker.find(
      dataListTrackerItem => lookup(dataListTrackerItem) === lookup(dataListItem),
    )
      ? true
      : false;

    if (isPresentInDataListTracker && !isRadio) {
      nextStateOfDataListTracker = nextStateOfDataListTracker.filter(
        item => lookup(item) !== lookup(dataListItem),
      );
    } else {
      if (!isMultiSelect) {
        nextStateOfDataListTracker = [];
      }
      nextStateOfDataListTracker.push(dataListItem);
    }

    setDataListTracker(nextStateOfDataListTracker);
    callback(nextStateOfDataListTracker, dataListItem, !isPresentInDataListTracker);
  };

  const isChecked = (item: T) => {
    return dataListTracker.find(dataListTrackerItem => lookup(dataListTrackerItem) === lookup(item))
      ? true
      : false;
  };

  const resetHandler = () => {
    setDataListTracker([]);
  };

  return [isChecked, onClickHandler, resetHandler];
};

export default useArrayToggler;
